import { orderBy } from 'lodash'

import Filter from 'modules/core/components/Filter'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { InsightsFilters } from 'modules/insights/hooks/useInsightsFilters'

const getFilterTypeLabel = (filterType: keyof InsightsFilters): string => {
  switch (filterType) {
    case 'genders':
      return i18n.t('insights.filters.types.gender.label')
    case 'legalEntities':
      return i18n.t('insights.filters.types.legal_entity.label')
    case 'workplaces':
      return i18n.t('insights.filters.types.workplace.label')
    case 'managers':
      return i18n.t('insights.filters.types.manager.label')
    case 'teams':
      return i18n.t('insights.filters.types.team.label')
    case 'nationalities':
      return i18n.t('insights.filters.types.nationality.label')
  }
}

type Option<T extends keyof InsightsFilters> = {
  label: string
  value: NonNullable<InsightsFilters[T]>[number]
}

type Props<T extends keyof InsightsFilters> = {
  filterType: T
  options: Option<T>[]
  filter: InsightsFilters[T]
  withEmptyOption?: boolean
  setFilter: (filterType: T, filter: InsightsFilters[T]) => void
}

const FilterSelector = <T extends keyof InsightsFilters>({
  filterType,
  options,
  filter,
  withEmptyOption,
  setFilter,
}: Props<T>) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  const handleNewValues = useUnsafeCallback(
    (newValues?: NonNullable<InsightsFilters[T]>[number][]) => {
      // TS detects newValues as an intersection of number & string array instead of a union
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      setFilter(filterType, newValues as InsightsFilters[T])

      track(trackingEvents.insights.filterSelected, {
        company_id: companyId,
        access_id: accessId,
        filterType: filterType,
      })
    },
    [filterType, setFilter]
  )

  const handleToggle = useUnsafeCallback(
    (open: boolean) => {
      if (open) {
        track(trackingEvents.insights.filterOpened, {
          company_id: companyId,
          access_id: accessId,
          filterType: filterType,
        })
      }
    },
    [filterType]
  )

  const selectedValuesSet = new Set<NonNullable<InsightsFilters[T]>[number]>(filter ?? [])
  const finalOptions = withEmptyOption
    ? [
        ...options,
        {
          label: i18n.t('insights.filters.not_defined'),
          value: null,
        },
      ]
    : options
  const sortedOptions = orderBy(
    finalOptions,
    (option) => selectedValuesSet.has(option.value),
    'desc'
  )

  return (
    <Filter.MultipleChoice<NonNullable<InsightsFilters[T]>[number]>
      label={getFilterTypeLabel(filterType)}
      options={sortedOptions}
      value={filter ?? []}
      onValueChange={handleNewValues}
      onToggle={handleToggle}
    />
  )
}

export default FilterSelector
